// This module implements a fully Bayesian approach to trendline estimation
// trendline is a simple linear regression of score on date (integer)
// prior comes from percentile rank data for the last observed score in the data array

// External Dependencies
import moment from 'moment';
import { locales } from '../constants/constants';

// Internal Dependencies
import { all_percentiles } from '../dummy_data/all_percentiles';
import { all_percentiles_spanish } from '../dummy_data/all_percentiles_spanish';


const scale_dict = {
  "NWF": "decodeScore",
  "ESRI": "esriScore",
  "EVS": "vocabSize",
  "PSF": "phonAwareScore",
  "AREA": "areaScore",
  "WCPM": "wcpmScore",
  "Lexile": "Lexile",
  "DRA": "DRA",
  "ARM": "ARM"
}

const grade_dict = {
  0: "Kindergarden",
  1: "Firstgrade",
  2: "Secondgrade",
  3: "Thirdgrade",
  4: "Fourthgrade",
  5: "Fifthgrade",
  6: "Sixthgrade"
}

function mean(inArray){
  if(inArray.length){
    return (arraySum(inArray)/inArray.length);
  }else{
    console.warn("Tried to get mean of array of length 0");
    return 0;
  }
}

function arraySum(inArray){
  return inArray.reduce(function (total, value){
    return total+value;
  },0);
}

// standard deviation
function sd(inArray){
  if(inArray.length){
    let sumX2 = inArray.reduce(function(total,value){
      return (total+Math.pow(value,2));
    },0);
    let sum = arraySum(inArray);
    let sd = Math.sqrt((sumX2 - (Math.pow(sum,2)/inArray.length))/(inArray.length-1));
    return sd;
  }else{
    console.warn("tried to do standard deviation on array of length 0");
    return 0;
  }
}

// Pearson product-moment correlation coefficient
// inX and inY are arrays.
function cor(inX,inY){
  if(inX.length && inY.length && (inX.length === inY.length)){
    let S_x = 0.0
    let S_x2 = 0.0
    let S_y = 0.0
    let S_y2 = 0.0
    let S_xy = 0.0

    inX.reduce((total,value,index)=>{
      S_x += value;
      S_x2 += Math.pow(value,2);
      S_y += inY[index];
      S_y2 += Math.pow(inY[index],2);
      S_xy += value*inY[index];
    },0);

    try{
      let r = (S_xy - (S_x*S_y)/inX.length) / Math.sqrt((S_x2 - (Math.pow(S_x,2))/inX.length) * (S_y2 - (Math.pow(S_y,2))/inY.length));
      return r;
    }catch(err){
      console.warn("X or Y has zero variance, setting cor to 0");
      return 0.0;
    }
  }else{
    console.warn("Tried to do correlation on arrays of different lengths or length 0");
    return 0.0;
  }
}

// Regress Y on X, simple linear regression
// produce predicted values for an X array called input
// X and Y are arrays of data
function regress(Y_data,X_data,input){
  try{
    let predicted = input.map((value)=>{
      return(mean(Y_data) + 0*value);
    });
    if (sd(Y_data) > 0 && sd(X_data) > 0) {
    let slope = cor(Y_data, X_data) * sd(Y_data)/sd(X_data);
    let inter = mean(Y_data) - slope*mean(X_data);

    predicted = input.map((value)=>{
      return(inter + slope*value);
    });
    }
    return predicted;
  }catch(err){
    //If std of x or y is 0, the trend line is a horizontal with a value
    // of y = mean(y). Explicitly, it's of the form x*0+mean(Y)
    console.warn("X or Y std is 0. Using mean of Y for regression");
    predicted = input.map((value)=>{
      return(mean(Y_data) + 0*value);
    });
  }
}

// This method takes in an array of tuples and an array of dates
// and returns an array of tuples that represent the regression
// scale, season, and grade are necessary to calculate Bayesian prior
export function calculateTrendline(datescorePairs, outputDates, scale, season, grade,locale){

  let scoreArray = [];
  //First, unpack the array of tuples into separate vectors for date and score, while
  // normalizing the dates to the output array
  let dateArray = datescorePairs.map((element)=>{
    //populate the score array here so we don't have to do another walk of the input array
    scoreArray.push(element[1]);

    // Put in terms of days since the start of the desired output array
    let daysSinceStart = moment(element[0]).diff(moment(outputDates[0]),'days');
    return daysSinceStart;
  });

  //Now, normalize the output dates vector the same way. Why do we do this? The numbers get Really big
  // if we leave in terms of the raw date and overflow some of the calculations.
  let xAxis = outputDates.map(date=>moment(date).diff(moment(outputDates[0]),'days'));

  //Get the regression line, save intercept & slope to data_coefficients
  let pred = regress(scoreArray,dateArray,xAxis);
  let data_coefficients = [pred[0], pred[1]-pred[0]];
  //pred[0] is the intercept (predicted value when xAxis==0) for the line, since the first value of dateArray is always 0
  //pred[1] - pred[0] gives us the slope of the line, as it is delta(Y) / delta(X) and delta(X) always == 1 (scale is 1 day)

  // Gather information for Bayesian prior trendline and
  // save array of 99 percentile values for the given season/scale/grade
  let graphql_scale_name = scale_dict[scale];
  let graphql_grade_name = grade_dict[grade];
  if(!graphql_grade_name) graphql_grade_name = grade_dict[6];

  const basePercentiles = locale === locales.ES_MX ? all_percentiles_spanish : all_percentiles;

  // search for "ARM SPANISH SCORES" to find other places where ARM score is determined.
  let perc = basePercentiles[season][graphql_scale_name.concat(graphql_grade_name)];

  // Find percentile_rank that matches most recent score (PR must be >= value in array)
  let N_points = scoreArray.length;
  let percentile_rank = 1;
  var p;
  for (p = 0; p < perc.length; p++) {
    if (scoreArray[N_points-1] >= perc[p]) {
      percentile_rank = p + 1;
    } else {
      break;
    }
  }

  // Find the Kth percentile rank score for fall, winter, spring for most recent observation
  // These 3 values will be the dependent variable (Y) used to determine the typical growth
  // rate for a kid at the Kth percentile rank. Independent variable (X) will be beginning,
  // middle, and end of year days, adjusted to that '0' is the day of first activity observed.
  let perc_trend_data = [
    all_percentiles["fall"][graphql_scale_name.concat(graphql_grade_name)][percentile_rank-1],
    all_percentiles["winter"][graphql_scale_name.concat(graphql_grade_name)][percentile_rank-1],
    all_percentiles["spring"][graphql_scale_name.concat(graphql_grade_name)][percentile_rank-1],
  ];

  // When student scores are regressed on days of observations, the dates are rescaled so that
  // the first activity day is 0, and the length of the pred array goes through the end of the year
  // Regression of perc_trend_data on beginning, middle, end of year needs to have the same 0,
  // where days of the year go from 0 to 364, and the offset value is 1 less than length of xAxis
  let days_offset_value = 364 - xAxis.length - 1;
  let centered_year_dates = [ 0-days_offset_value, 182-days_offset_value, 364-days_offset_value ];
  let bayes_line = regress(perc_trend_data,centered_year_dates,xAxis);

  // This adjustment to how the Bayesian coefficients are stored ensures good fit to data points, especially when scores are "up and down"
  let bayes_coefficients = [mean(scoreArray), bayes_line[1]-bayes_line[0]];

  // Now we have intercept and slope of the observed data trendline and the bayesian line (based on typical growth rate)
  // The posterior distribution of coefficients is a weighted average of the two vectors.
  // Prior (bayesian_coefficients) has weight == 364 because it contains observations over the course of year; data (data_coefficients) are weighted
  // NOT by N_points, as before, but by how many days worth of data we have. E.g., six activities on one day only count as 1, as they don't imply a trend.
  // So, with many observations OVER TIME, data have much more influence than the Prior. The weight is a proportion such that:
  // weight(data) + weight(prior) == 1. So the `weight` variable represents how much influence the data have
  let weight = dateArray[N_points-1] / (dateArray[N_points-1] + 364);
  let posterior_coefficients = [];
  posterior_coefficients[0] = weight*(data_coefficients[0]) + (1-weight)*bayes_coefficients[0];
  posterior_coefficients[1] = weight*(data_coefficients[1]) + (1-weight)*bayes_coefficients[1];

  // The new set of predictions map the original xAxis to our new posterior coefficients line,
  // which incorporates information from the data mixed with the Bayesian prior
  pred = xAxis.map((value)=>{
    return(posterior_coefficients[0] + posterior_coefficients[1]*value);
  });


  //Lastly, build the output tuples
  let outTuples = outputDates.map((date,index)=>{
    let tuple = [date];
    tuple.push(pred[index]);
    return tuple;
  });

  return outTuples;
}
